/* global t, pkLabeled, safeArray, num, mergedRuntimeState, buildPeriodCompleteness, buildPeriodAnalytics, buildPeriodTasks, buildPeriodReports, buildBankReconciliationView, buildLedgerLines, pkPeriodRange, pkMoney, pkMasterControls, pkCrossChecks, pkAutoChecks, pkChecklist, pkSummary, pkBlockers */
// PERIODEKONTROLLER — lesemodellen bak kontrolltårnet
//
// Området har ingen konto og ingen saldo. Alt det viser er UTLEDET av de ti
// andre områdenes egne avstemminger, av bokføringen og av kontrollen. Det er
// hele grunnen til at skjermen finnes: den er det eneste stedet som kan si om
// PERIODEN er ferdig, ikke bare en konto.
//
// Derfor krysses ingenting av her. Statusen på et avstemmingsområde hentes fra
// området selv; et hakk her ville kunnet si noe annet enn avstemmingen gjør.
// De eneste tingene et menneske skriver på denne skjermen er ordene ingen
// utledning kan produsere: forklaringen på et analytisk avvik, bekreftelsen på
// en faglig tverrkontroll og konklusjonen for perioden. De lagres på
// periodedokumentet (`periodReview`), ikke her.
//
// Modellen er READ-ONLY og har ingen mockdata.

const PK_TOLERANCE = 0.005;
const pkNum = (value) => Math.round((num(value) + Number.EPSILON) * 100) / 100;
// Speiler CHECKLIST_ONLY_AREAS i functions/reconciliation/reconciliationChecklist.ts.
const PK_CHECKLIST_ONLY_AREAS = ['periodekontroll'];

// Labels are getters (see pkLabeled) so they follow the UI language at render time.
const PK_AREA_STATUS = {
  ikke_kontrollert: pkLabeled('rcd.areaStatus.notStarted', 'Ikke påbegynt', { cls: 'muted' }),
  under_arbeid: pkLabeled('rcd.areaStatus.inProgress', 'Under arbeid', { cls: 'warning' }),
  venter_periodeslutt: pkLabeled('rcd.areaStatus.waitingPeriodEnd', 'Venter på periodeslutt', { cls: 'warning' }),
  avvik: pkLabeled('rcd.areaStatus.deviation', 'Avvik', { cls: 'error' }),
  ok: pkLabeled('rcd.areaStatus.approved', 'Godkjent', { cls: 'success' }),
  ikke_relevant: pkLabeled('rcd.areaStatus.notRelevant', 'Ikke relevant', { cls: 'muted' }),
};

const PK_RESULT = {
  bestatt: pkLabeled('rcd.result.passed', 'Bestått', { cls: 'success' }),
  venter: pkLabeled('rcd.result.waiting', 'Venter', { cls: 'warning' }),
  avvik: pkLabeled('rcd.result.deviation', 'Avvik', { cls: 'error' }),
  blokkert: pkLabeled('rcd.result.blocked', 'Blokkert', { cls: 'error' }),
  ikke_vurdert: pkLabeled('rcd.result.notAssessed', 'Ikke vurdert', { cls: 'muted' }),
};

const PK_TYPE_LABEL = {
  get auto() { return t('rcd.type.auto', 'Automatisk kontroll'); },
  get faglig() { return t('rcd.type.professional', 'Faglig vurdering'); },
  get kontrollor() { return t('rcd.type.controller', 'Kontrollørkontroll'); },
};

// Åpne poster i et område: en linjevurdering som fortsatt har et tiltak som
// ikke er løst, og en avstemmingspost med et tiltak som ikke er kvittert ut.
// Det er noe ANNET enn en oppgave: samme forhold kan gi én oppgave, og en
// oppgave kan lukke flere poster.
const pkOpenItems = (account = {}) => {
  const lines = Object.values(account.lineAssessments || {})
    .filter((entry) => String(entry?.tiltak || '').trim() && entry?.tiltakStatus !== 'lost').length;
  const items = safeArray(account.explainedItems)
    .filter((item) => String(item?.action || '').trim() && item.action !== 'Ingen' && item?.resolved !== true).length;
  return lines + items;
};

const pkAreaRow = ({ area, accounts, status, responsibleName, vatTerm }) => {
  const rows = safeArray(accounts).filter((account) => (account.areaKey || 'ovrig') === area.key);
  const checklistOnly = PK_CHECKLIST_ONLY_AREAS.includes(area.key);
  const relevant = rows.filter((account) => account.status !== 'ikke_relevant');
  const unexplained = relevant.reduce((sum, account) => (
    account.difference === null || account.difference === undefined ? sum : sum + Math.abs(num(account.difference))
  ), 0);
  const compared = relevant.filter((account) => account.difference !== null && account.difference !== undefined).length;
  const missingDocs = checklistOnly
    ? 0
    : relevant.filter((account) => !account.documentId).length;
  const apnePoster = rows.reduce((sum, account) => sum + pkOpenItems(account), 0);
  const touched = rows.some((account) => account.status !== 'ikke_kontrollert')
    || rows.some((account) => safeArray(account.checklist).some((item) => item.status !== 'ikke_kontrollert'));

  // MVA venter på at terminen er over. Det er ikke arbeid som mangler — det er
  // en termin som ikke kan ferdigstilles ennå, og de to skal ikke se like ut.
  let display = status;
  if (area.key === 'mva' && vatTerm && !vatTerm.isTermEnd && status !== 'ok') display = 'venter_periodeslutt';
  else if (status === 'ikke_kontrollert' && touched) display = 'under_arbeid';

  let saldo = t('rcd.area.noExternalBalance', 'Ingen ekstern saldo å sammenlikne mot');
  if (checklistOnly) saldo = t('rcd.area.noBalanceChecklist', 'Ingen saldo — området er kontrollpunkter');
  else if (unexplained > PK_TOLERANCE) saldo = t('rcd.area.unexplained', '{amount} uforklart', { amount: pkMoney(unexplained) });
  else if (compared > 0) saldo = t('rcd.area.matchesLedger', 'Stemmer mot hovedbok');

  const oppdatert = rows.map((account) => String(account.updatedAt || '')).filter(Boolean).sort().pop() || '';

  return {
    key: area.key,
    label: area.displayLabel || area.label,
    omfang: checklistOnly
      ? t('rcd.area.checkpoints', 'Kontrollpunkter for perioden')
      : (rows.length ? (rows.length === 1 ? t('rcd.area.accountOne', '{count} konto', { count: rows.length }) : t('rcd.area.accountMany', '{count} kontoer', { count: rows.length })) : t('rcd.area.noAccounts', 'Ingen kontoer')),
    status: display,
    statusLabel: (PK_AREA_STATUS[display] || PK_AREA_STATUS.ikke_kontrollert).label,
    statusCls: (PK_AREA_STATUS[display] || PK_AREA_STATUS.ikke_kontrollert).cls,
    saldo,
    unexplained: pkNum(unexplained),
    apnePoster,
    dok: checklistOnly ? '—' : (missingDocs ? t('rcd.area.docsMissing', '{count} mangler', { count: missingDocs }) : (relevant.length ? t('rcd.area.docsComplete', 'Komplett') : '—')),
    missingDocs,
    ansvarlig: responsibleName || '',
    oppdatert,
    total: rows.length,
    godkjent: rows.filter((account) => account.status === 'ok').length,
    avvik: rows.filter((account) => account.status === 'avvik').length,
    basisChanged: rows.some((account) => account.basisChangedAfterReconciliation === true),
    checklistOnly,
  };
};

const buildPeriodControlReconciliationView = ({
  periodId, periodLabel = '', areas = [], accounts = [], areaStatuses = {},
  requests = [], period = null, responsibleName = '', state: suppliedState = null,
  basis = 'forrige_maned', today = new Date(),
} = {}) => {
  const state = suppliedState || (typeof mergedRuntimeState === 'function' ? mergedRuntimeState() : null);
  const range = pkPeriodRange(periodId);
  const todayIso = today.toISOString().slice(0, 10);
  const periodEnded = Boolean(range) && todayIso > range.to;
  const dataThrough = range ? (periodEnded ? range.to : todayIso) : '';
  const review = period?.periodReview || {};
  const vatTerm = period?.vatTerm || null;

  const others = safeArray(areas).filter((area) => area.key !== 'periodekontroll');
  const rows = others.map((area) => pkAreaRow({
    area, accounts, status: areaStatuses[area.key] || 'ikke_kontrollert', responsibleName, vatTerm,
  }));

  let bankCounts = {};
  try {
    if (typeof buildBankReconciliationView === 'function') {
      bankCounts = buildBankReconciliationView({ periodId, accounts: accounts.filter((account) => account.areaKey === 'bank'), state })?.counts || {};
    }
  } catch (error) {
    bankCounts = {};
  }

  const completeness = buildPeriodCompleteness({ periodId, state, bankCounts });
  const analytics = buildPeriodAnalytics({ periodId, state, basis, explanations: review.analytics || {} });
  const inboxCount = completeness.find((entry) => entry.id === 'inbox')?.value || 0;
  const tasks = buildPeriodTasks({
    areas, accounts, requests, inboxCount,
    materialityThreshold: period?.materialityThreshold ?? null, today: todayIso,
  });
  const allAreasDone = rows.every((row) => ['ok', 'ikke_relevant'].includes(row.status));
  const reports = buildPeriodReports({
    periodId, periodLabel, period, state, areaStatuses, allAreasDone, responsibleName,
  });

  const blockerGroups = pkBlockers({ rows, completeness, analytics, vatTerm, periodEnded });
  const blockers = blockerGroups.flatMap((group) => group.items.map((text) => ({ area: group.gruppe, text })));

  const periodAccount = safeArray(accounts).find((account) => (account.areaKey || '') === 'periodekontroll') || null;
  const auto = pkAutoChecks({ rows, completeness, analytics, reports, blockers, areaCount: others.length });
  const checklist = pkChecklist({ periodAccount, auto });
  const allItems = checklist.flatMap((group) => group.items);

  const ledgerBalance = (() => {
    if (!range || typeof buildLedgerLines !== 'function') return null;
    try {
      const lines = buildLedgerLines({ from: range.from, to: range.to, state });
      return lines.length ? pkNum(lines.reduce((sum, line) => sum + num(line.amount), 0)) : null;
    } catch (error) {
      return null;
    }
  })();

  const crossChecks = pkCrossChecks({ rows, accounts, ledgerBalance, confirmations: review.crossChecks || {} });
  const checklistSummary = pkSummary(allItems);

  return {
    periodId,
    periodLabel,
    dataThrough,
    periodEnded,
    // Foreløpig så lenge måneden løper. En periodekontroll midt i måneden er
    // ikke feil — men den er ikke endelig, og den forskjellen skal stå skrevet.
    preliminary: !periodEnded,
    vatTerm,
    materialityThreshold: period?.materialityThreshold ?? null,
    materialityBasis: period?.materialityBasis || '',
    locked: period?.locked === true,
    responsibleName,
    areas: rows,
    summary: {
      relevante: rows.length,
      ferdig: rows.filter((row) => ['ok', 'ikke_relevant'].includes(row.status)).length,
      avvik: rows.filter((row) => row.status === 'avvik').length,
      apnePoster: rows.reduce((sum, row) => sum + row.apnePoster, 0),
      manglendeDok: rows.reduce((sum, row) => sum + row.missingDocs, 0),
      uposterte: inboxCount,
      aktiveOppgaver: tasks.filter((task) => task.status !== 'lost').length,
      uforklarteAnalyser: safeArray(analytics.rows).filter((row) => !row.forklaring).length,
      ubehandledePunkter: checklistSummary.total - checklistSummary.behandlet,
    },
    // Arbeidsfremdrift måler hvor mye som er GJENNOMGÅTT, ikke hvor mye som er
    // bestått. Et behandlet punkt kan fortsatt ha avvik, og de to tallene skal
    // aldri slås sammen: da ville en periode se ferdig ut fordi noen hadde
    // krysset av på at den var full av feil.
    progress: {
      behandletPct: checklistSummary.total
        ? Math.round((checklistSummary.behandlet / checklistSummary.total) * 100)
        : 0,
      klarForRapportering: blockers.length === 0,
      blockerCount: blockers.length,
    },
    masterControls: pkMasterControls({ rows, blockers }),
    crossChecks,
    completeness,
    analytics,
    tasks,
    reports,
    checklist,
    checklistSummary,
    blockerGroups,
    blockers,
    conclusion: review.conclusion || null,
    periodAccountId: periodAccount?.accountId || null,
  };
};

Object.assign(window, {
  buildPeriodControlReconciliationView,
  PK_AREA_STATUS, PK_RESULT, PK_TYPE_LABEL,
});
